import { ArrowLeft, ArrowRight, CalendarDate, ChevronDown } from '@untitled-ui/icons-react/build/cjs';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DateRangePicker from 'src/components/DateRangePicker';
import SelectWithActionTooltip from 'src/components/SelectWithActionTooltip';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { createDateWithoutTimezoneConversion } from 'src/utils/CommonFunctions';

import { TRACK_DATE_PRESET, TRACK_DATE_PRESET_OPTIONS } from './track.const';
import { getActiveTrackDatePreset, getTrackDateRangeLabel, getTrackPresetDateRange, shiftTrackDateRange } from './track.helpers';
import { setTrackParams, trackParams as trackParamsSelector } from './track.slice';

interface IProps {
    disabled?: boolean;
    // Outer wrapper classes. The map Overview header wants a bare inline control; the Table
    // View filter bar passes the dashed/solid chip border so it matches its sibling filters.
    wrapClassName?: string;
    // Classes for the preset menu's popover box. Only the Table View needs this: its filter bar
    // is a short overflow-x-auto row, which popper treats as the menu's overflow boundary and so
    // clamps the popover back up over the trigger. The call site nudges it down with a margin
    // instead of touching the shared .action-tooltip-select-wrap rule, so the map Overview
    // header — where the menu already drops correctly — is untouched.
    dropdownWrapClassName?: string;
}

// Track date control, shared by the map Overview header and the Table View filter bar —
// ported 1:1 from WAL radar's RadarDateRangeQuickPicker: one trigger showing the active
// window, with < > arrows that step it and a dropdown of quick ranges (Today / Tomorrow /
// This Week / This Month / Custom). "Custom" opens the range calendar; every other option
// commits its dates immediately. Replaces the plain DateRangePicker, which forced the user
// into the calendar for even the commonest ranges.
const TrackDateRangeQuickPicker = ({ disabled = false, wrapClassName = '', dropdownWrapClassName = '' }: IProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const trackParams = useAppSelector(trackParamsSelector);
    // Timezone lives on basic.appearance, not on auth — reading state.auth.timezone silently
    // yields undefined and falls back to UTC, which shifts "today" a day for western zones.
    const userTimezone = useAppSelector((state) => state.basic.appearance?.timezone) || 'UTC';

    const [isPresetMenuOpen, setIsPresetMenuOpen] = useState(false);
    // Opens the range calendar; only the "Custom" option sets this, so the quick presets
    // never have to route through the calendar.
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);

    // Switching to the In-Transit stage disables this control. Close anything already open —
    // otherwise a menu/calendar opened just before the switch stays on screen and usable.
    useEffect(() => {
        if (disabled) {
            setIsPresetMenuOpen(false);
            setIsCalendarOpen(false);
        }
    }, [disabled]);

    const activePreset = useMemo(() => getActiveTrackDatePreset(trackParams, userTimezone), [trackParams, userTimezone]);
    const displayLabel = useMemo(() => getTrackDateRangeLabel(trackParams, userTimezone, t), [trackParams, userTimezone, t]);

    const presetOptions = useMemo(() => TRACK_DATE_PRESET_OPTIONS.map((opt) => ({ value: opt.value, label: String(t(opt.labelKey as any)) })), [t]);

    // Single commit point for every date change, whichever control (arrow, preset,
    // calendar) triggered it. No preset is stored: the label and the arrow stride are both
    // derived from the dates themselves, so picking "Today" from the dropdown and landing
    // on today's date via Custom/arrows are identical. page resets like every other filter.
    const commitDateRange = (startDate: string, endDate: string) => {
        dispatch(setTrackParams({ startDate, endDate, page: 1 }));
    };

    const handleArrowClick = (direction: 1 | -1) => {
        if (disabled) return;

        const shifted = shiftTrackDateRange(trackParams?.startDate, trackParams?.endDate, direction);
        commitDateRange(shifted.startDate, shifted.endDate);
    };

    const handlePresetSelect = (preset: string) => {
        if (disabled) return;

        setIsPresetMenuOpen(false);

        if (preset === TRACK_DATE_PRESET.custom) {
            // DateRangePicker mirrors isOpenDatePicker into its own state only when the prop
            // CHANGES, and it closes itself on outside-click/scroll without telling us. Pulse
            // false→true so re-picking Custom always reopens it, even after such a silent close.
            setIsCalendarOpen(false);
            setTimeout(() => setIsCalendarOpen(true), 0);

            return;
        }

        const range = getTrackPresetDateRange(preset, userTimezone);
        commitDateRange(range.startDate, range.endDate);
    };

    const handleCalendarChange = (dates: any) => {
        if (disabled) return;

        const [start, end] = dates ?? [];

        // react-datepicker range mode returns [start, null] when From and To are the same
        // day, so fall back to start as the end — same guard as the other Track pickers.
        if (start) {
            const rangeEnd = end ?? start;
            commitDateRange(moment(start).format('YYYY-MM-DD'), moment(rangeEnd).format('YYYY-MM-DD'));
        }

        setIsCalendarOpen(false);
    };

    // Hover wash on the whole trigger (icon + label + chevron) with padding so the pill has
    // a real surface, rather than just the text, matching the arrows' square hit areas
    // either side. utilityGray100 (not gray50): this control sits on the white Overview
    // card, where gray50's #F9FAFB is a ~2% delta and reads as no hover at all.
    // Mirrors WAL's RadarDateRangeQuickPicker.
    const triggerLabel = (
        <div
            className={`flex items-center gap-x-1 whitespace-nowrap rounded-md px-1.5 py-1 transition-colors ${disabled ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer hover:bg-utilityGray100'}`}
        >
            <CalendarDate className="w-4 h-4 flex-none text-grayLight600" />
            <span className="text-xs font-medium text-grayLight900">{displayLabel}</span>
            <ChevronDown className="w-3.5 h-3.5 flex-none text-grayLight600" />
        </div>
    );

    return (
        <div className={`relative flex items-center gap-x-1 ${wrapClassName}`}>
            {/* The arrows are wrapped rather than styled directly: a 16px icon is too small a
                hover target, so the padded span provides both the hit area and the surface the
                hover wash paints — the icon still darkens on top of it. */}
            <span className={`flex rounded-md p-1 transition-colors ${disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-utilityGray100 group/prev'}`} onClick={() => handleArrowClick(-1)}>
                <ArrowLeft className={`w-4 h-4 flex-none transition-colors ${disabled ? 'text-gray300' : 'text-grayLight600 group-hover/prev:text-grayLight900'}`} />
            </span>

            {/* Disabled (In-Transit stage): render the label alone. SelectWithActionTooltip has no
                disabled prop and opens on its own click, so styling the label was not enough to
                stop the menu — the trigger has to be left out of the tooltip wrapper entirely. */}
            {disabled ? (
                triggerLabel
            ) : (
                <SelectWithActionTooltip
                    isOpenChildTopPosOnTopFalse={true}
                    selectTooltipParentClassName={dropdownWrapClassName}
                    actionTooltipSelectWrap="min-w-[150px]"
                    selectTooltipPosition="center"
                    selectBoxHeading={true}
                    selectBoxHeadingLabel={String(t('Radar.Date_Range'))}
                    options={presetOptions}
                    selectedOptionValue={activePreset}
                    onSelectOption={handlePresetSelect}
                    isMenuClickedKeepsTooltipOpen={isPresetMenuOpen}
                    setIsMenuClickedKeepsTooltipOpen={setIsPresetMenuOpen}
                    label={triggerLabel}
                />
            )}

            <span className={`flex rounded-md p-1 transition-colors ${disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-utilityGray100 group/next'}`} onClick={() => handleArrowClick(1)}>
                <ArrowRight className={`w-4 h-4 flex-none transition-colors ${disabled ? 'text-gray300' : 'text-grayLight600 group-hover/next:text-grayLight900'}`} />
            </span>

            {/* Calendar for the "Custom" option. It stays mounted and absolutely overlays the
                label so DateRangePicker's own dropdown positioning anchors to the real
                on-screen box; pointer-events-none keeps it click-through (the label's
                dropdown owns clicks) while isOpenDatePicker drives it open from Custom.
                CP's DateRangePicker lacks WAL's disabled/dropdownZIndex/isDisplayAppliedDatesOnly
                props — disabled is enforced by this component (calendar force-closed above),
                and the z-index rides on containerClassName instead. */}
            <div className="absolute inset-x-0 bottom-0 h-0 pointer-events-none">
                <DateRangePicker
                    handleDatePickerChange={handleCalendarChange}
                    selectedTimeRange="custom"
                    startDate={trackParams?.startDate ? createDateWithoutTimezoneConversion(moment(trackParams.startDate)) : null}
                    endDate={trackParams?.endDate ? createDateWithoutTimezoneConversion(moment(trackParams.endDate)) : null}
                    allTimeStartDate={trackParams?.startDate ? createDateWithoutTimezoneConversion(moment(trackParams.startDate)) : null}
                    isShowDropDownIcon={false}
                    hideTimeRangeSelect={true}
                    showYearDropdown={false}
                    isOpenDatePicker={isCalendarOpen}
                    handleDatePickerClose={() => setIsCalendarOpen(false)}
                    dateRangeWrapClass="!border-0 !shadow-none !bg-transparent !p-0 !h-auto !min-h-0 invisible"
                    containerClassName="!z-[9999]"
                />
            </div>
        </div>
    );
};

export default TrackDateRangeQuickPicker;
